import { ProductCard } from '../commerce/ProductCard.jsx';
import { SkeletonCard } from '../ui/Skeleton.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { cn } from '../../lib/format.js';

/**
 * The home page's product shelf: two across on a phone, five on a desktop, the
 * same gap everywhere so consecutive shelves line up column for column.
 *
 * Skeletons hold the grid's exact footprint while the query is in flight. What
 * happens when it comes back empty is the caller's decision — some shelves say
 * so, others simply step aside — so `empty` is optional.
 */

const GRID = 'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5';

/**
 * A shelf never ends on a part-filled row. At each breakpoint, once there is at
 * least one full row, the cards that would spill onto an unfinished last row
 * are hidden — six products on a five-across desktop show five, while the same
 * six still fill three rows of two on a phone. Below one full row everything
 * shows, because a short shelf is better than an empty one.
 *
 * The wrapper uses `display: contents`, so a visible card is laid out by the
 * grid exactly as it would be without it.
 */
const COLUMNS = [
  { cols: 2, show: 'contents', hide: 'hidden' },
  { cols: 3, show: 'sm:contents', hide: 'sm:hidden' },
  { cols: 5, show: 'lg:contents', hide: 'lg:hidden' },
];

const visibility = (index, total) =>
  COLUMNS.map(({ cols, show, hide }) => {
    if (total < cols) return show;
    return index < Math.floor(total / cols) * cols ? show : hide;
  }).join(' ');

export const ProductGrid = ({ products, isPending, limit = 10, skeletons = 5, empty, onQuickAdd, priority = false, className }) => {
  if (isPending) {
    return (
      <div className={cn(GRID, className)}>
        {Array.from({ length: skeletons }, (_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    );
  }

  if (!products?.length) {
    return empty ? <EmptyState {...empty} /> : null;
  }

  const shown = products.slice(0, limit);

  return (
    <div className={cn(GRID, className)}>
      {shown.map((product, index) => (
        <div key={product._id} className={visibility(index, shown.length)}>
          <ProductCard
            product={product}
            priority={priority && index < 5}
            onQuickAdd={onQuickAdd?.(product)}
          />
        </div>
      ))}
    </div>
  );
};

export default ProductGrid;
